import type { ReactNode } from "react";
import { createRoot, type Root } from "react-dom/client";
import App from "./App.tsx";
import livekitStyles from "@livekit/components-styles?inline";
import styles from "./index.css?inline";
import { isScreenSizeMobile } from "./utils/isMobile.ts";
import { darkenHex } from "./utils/darkenHex.ts";
import { discoverTemplates, containerDeclaresTemplates, TemplatesProvider } from "./templating";
import { applyHostStyling } from "./styling/loadExternalStylesheet.ts";
import { LastBotCore, API_VERSION, type InitResult } from "./core/LastBotCore";
import type { CoreConfig } from "./core/types";
import type { LastBotState } from "./core/state";
import { useCoreState } from "./contexts/useCoreState";

declare global {
  interface Window {
    LastBot: LastBotGlobal;
  }
}

interface LastBotGlobal {
  /**
   * Create a `LastBotCore` instance programmatically. Returns the core *before* `init()` has
   * resolved — register event listeners synchronously, then `await core.init()` (or listen
   * for the `lastbot:ready` CustomEvent if you're working with the custom element instead).
   * Headless customers use this entry to skip the custom element entirely.
   */
  create: (_config: CoreConfig) => LastBotCore;
  /**
   * React hook: subscribes a component to a selected slice of core state via
   * `useSyncExternalStore`. Re-renders only when the selector's result changes by
   * reference equality.
   *
   *   const messages = LastBot.useCoreState(core, s => s.thread?.messages ?? []);
   *
   * Caveat for IIFE-bundle embedders: this hook runs against the bundle's bundled React.
   * If your app uses its own React, the rules of hooks break across React copies. For
   * mixed-React setups, use `core.bind()` or `core.subscribe()` directly until a proper
   * ESM package ships in a future release.
   */
  useCoreState: <T>(
    _core: LastBotCore,
    _selector: (_state: LastBotState) => T,
  ) => T;
  /**
   * Look up a live `LastBotCore` for a placement. Pass the placement's `placement-id`
   * (or, for a placement with no `placement-id`, its `widget-id` — the effective key).
   * Returns `undefined` when no such placement is currently mounted, so callers can
   * safely optional-chain:
   *
   *   window.LastBot.getCore({ placementId: 'inline-b' })?.sendMessage('Hello');
   *
   * The returned core may not have finished `init()` yet — for a call you need to
   * succeed immediately, wait for that placement's `lastbot:ready` first (its
   * `detail.placementId` identifies it). Only tracks custom-element / `[data-lastbot]`
   * placements; headless `create()` callers already hold their own core reference. If
   * two placements share a key (which logs a warning), the first-mounted one is returned.
   */
  getCore: (_opts: { placementId: string }) => LastBotCore | undefined;
  readonly apiVersion: number;
  readonly version: string;
}

const BUILD_VERSION = "1.0.0";

// Registry of live cores per effective placement key (placement-id || widget-id) across
// all mounted <lastbot-chat> elements. Serves two purposes: warn when two placements share
// a key (same-widget duplicates share per-placement storage; neither can be targeted
// independently), and back `window.LastBot.getCore({ placementId })` so host pages can
// address a specific placement's core. An array per key (mount order) so mount/teardown
// churn of one placement doesn't drop a still-live duplicate, and so lookup returns a
// deterministic (first-mounted) instance.
const placementRegistry = new Map<string, LastBotCore[]>();

function registerPlacement(key: string, core: LastBotCore): void {
  const cores = placementRegistry.get(key);
  if (cores) {
    cores.push(core);
    console.warn(
      `[lastbot-chat] Multiple placements on this page share the effective placement key "${key}". ` +
        "They cannot be targeted independently via postMessage/getCore(); placements of the same " +
        "widget also share per-placement UI/session state (current page, login-return page). " +
        "Give each placement a distinct placement-id attribute. " +
        "See docs/api.md § Multiple placements on one page.",
    );
  } else {
    placementRegistry.set(key, [core]);
  }
}

function unregisterPlacement(key: string, core: LastBotCore): void {
  const cores = placementRegistry.get(key);
  if (!cores) return;
  const remaining = cores.filter(c => c !== core);
  if (remaining.length === 0) {
    placementRegistry.delete(key);
  } else {
    placementRegistry.set(key, remaining);
  }
}

/**
 * Apply one length-valued CSS var to the shadow host from a `setSize`/`setPosition`
 * payload: `undefined` leaves the current value, `null` clears the inline override
 * (reverting to the index.css default), and any other value is set only if
 * `CSS.supports(cssProp, value)` accepts it — so a malformed value can't clobber a
 * working default. Shared by `applySize` and `applyPosition` so they can't drift.
 */
function setHostLengthVar(
  host: HTMLElement,
  cssProp: string,
  varName: string,
  value: string | null | undefined,
): void {
  if (value === undefined) return;
  if (value === null) {
    host.style.removeProperty(varName);
  } else if (CSS.supports(cssProp, value)) {
    host.style.setProperty(varName, value);
  }
}

function getCore(opts: { placementId: string }): LastBotCore | undefined {
  // Return the first LIVE core for this key: a mounted host that is still connected and a
  // core that hasn't been destroyed. This skips a placement mid-teardown (its element is
  // removed but the deferred-microtask unregister hasn't run yet) so a still-live duplicate
  // wins, and skips a core an embedder destroyed directly while its element stayed mounted.
  return placementRegistry.get(opts.placementId)?.find(core => {
    const host = core.getHostElement();
    return !core.isDestroyed && !!host && host.isConnected;
  });
}

if (typeof window !== "undefined" && !window.LastBot) {
  window.LastBot = {
    create: (config: CoreConfig) => new LastBotCore(config),
    useCoreState,
    getCore,
    apiVersion: API_VERSION,
    version: BUILD_VERSION,
  };
}

/**
 * Standard HTML Boolean attribute semantics for `<lastbot-chat>` flags: present → true
 * (`<lastbot-chat skip-ingest>`, `skip-ingest=""`, `skip-ingest="true"`), absent → false.
 * An explicit `"false"` / `"0"` (case-insensitive) opts out, so the legacy
 * `auto-open="false"` form and `[data-lastbot]` forwarding keep working.
 */
export function parseBooleanAttribute(el: Element, name: string): boolean {
  const value = el.getAttribute(name);
  if (value === null) {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return normalized !== "false" && normalized !== "0";
}

class Widget extends HTMLElement {
  private root: Root | null = null;
  private coreInstance: LastBotCore | null = null;
  private placementKey: string | null = null;
  private inlineHost: HTMLDivElement | null = null;
  private headlessMode = false;
  private brandBridgeCleanup: (() => void) | null = null;
  private setSizeCleanup: (() => void) | null = null;
  private setPositionCleanup: (() => void) | null = null;

  /**
   * The LastBotCore instance backing this custom element. Returns `null` before
   * `connectedCallback` has run. Embedders should prefer the `lastbot:ready` CustomEvent
   * (fired once core.init() resolves) so they can register handlers before the first
   * server response arrives.
   */
  public get core(): LastBotCore | null {
    return this.coreInstance;
  }

  public connectedCallback(): void {
    // Custom elements fire connect/disconnect/connect during DOM moves. If we already
    // have a core, this is a re-connect — keep the existing state.
    if (this.coreInstance) {
      return;
    }

    const widgetId = this.getAttribute("widget-id") || "";
    // Optional embed-placement identity. `widget-id` is the bot; `placement-id`
    // distinguishes two placements of the same bot on one page. Empty string → treated
    // as absent; the effective placement key falls back to widget-id. Forwarded from a
    // `[data-lastbot]` container automatically via applyContainerConfig (data-placement-id).
    const placementId = this.getAttribute("placement-id") || "";
    const startMessage = this.getAttribute("start-message") || "";
    const userEmail = this.getAttribute("user-email") || "";
    const homeUrl = this.getAttribute("home-url") || undefined;
    const skipIngest = parseBooleanAttribute(this, "skip-ingest");
    // Optional signed token identifying the chatter to the backend when the
    // widget is embedded inside the LastBot Hub for the support bot. Forwarded
    // as `hub_identity` to POST /api/v1/message_threads, where the Rails app
    // verifies the signature and scopes Hub tools to the resolved user.
    const hubIdentity = this.getAttribute("data-hub-identity") || this.getAttribute("hub-identity") || "";
    const baseUrl = this.getAttribute("base-url") || "";

    this.headlessMode = parseBooleanAttribute(this, "headless");

    // Required attributes guard. Without these the core would POST against
    // the embedder's own origin (relative `/api/v1/widget_tokens`) and use a
    // blank widget id, producing confusing 404s from the host server instead
    // of a clear local configuration error. Bail out early with a console
    // error and a `lastbot:error` lifecycle event so embedders see the
    // problem at their own logging surface.
    if (!baseUrl || !widgetId) {
      const missing = [
        !baseUrl ? "base-url" : null,
        !widgetId ? "widget-id" : null,
      ]
        .filter(Boolean)
        .join(", ");
      console.error(
        `[lastbot-chat] missing required attribute(s): ${missing}. Widget will not initialize.`,
      );
      // Dispatch a synchronous error event so listeners (especially ones
      // attached before the script loaded) see it. The detail has no `core`
      // because we never constructed one. Fired on `window` to match the
      // headless dispatch pattern in LastBotCore.maybeDispatchLifecycleEvent.
      window.dispatchEvent(
        new CustomEvent("lastbot:error", {
          detail: {
            core: null,
            status: undefined,
            reason: "missing-required-attribute",
            widgetId: widgetId || undefined,
            placementId: placementId || undefined,
          },
        }),
      );
      return;
    }

    this.coreInstance = new LastBotCore({
      baseUrl,
      widgetId,
      placementId: placementId || undefined,
      userEmail,
      startMessage,
      hubIdentity,
      skipIngest,
      homeUrl,
    });
    // Hand the host element to core so the v2 slot-event dispatcher can
    // fire CustomEvents here for embedder listeners to catch.
    this.coreInstance._setHostElement(this);

    // Register this placement's core under its effective placement key, so
    // window.LastBot.getCore({ placementId }) can address it and a second placement resolving to the same
    // key surfaces a console warning. Unregistered on teardown below.
    this.placementKey = placementId || widgetId;
    registerPlacement(this.placementKey, this.coreInstance);

    if (this.headlessMode) {
      // No default UI will ever mount for this core — tell it so open()'s
      // promise resolves immediately instead of waiting for a panel-readiness
      // ack that can never arrive (LAS-2166). The host element stays set
      // above: slot-event dispatch and getCore()'s liveness check need it.
      this.coreInstance._setHeadless(true);
      // Embedder owns rendering entirely. Init standalone, then signal.
      // Capture the local core ref so a teardown-mid-init (or a re-attach with a
      // fresh core) can't dispatch the lifecycle event against the wrong instance.
      const initCore = this.coreInstance;
      void initCore.init().then(result => {
        this.dispatchLifecycleEvent(result, initCore);
      });
      return;
    }

    // Container is read once at mount. To relocate at runtime, remove this <lastbot-chat>
    // element and add a new one with the new `container` attribute — persisted thread +
    // contact in localStorage make the new instance pick up the same conversation. For
    // zero-flicker relocation, use headless mode and own the renderer. See docs/api.md
    // § "Relocating the widget".
    // `inline="true"` renders the widget in-flow into <lastbot-chat> itself — the
    // element fills its parent instead of floating as an overlay, and no separate
    // `container=` target is needed. This is how the [data-lastbot] embedded path
    // mounts: `scanAndMount` synthesizes a `<lastbot-chat inline="true">` inside the
    // [data-lastbot] div, so the chat fills that div. Overlay-only settings
    // (auto-open, fullscreen, hide-toggle-button) don't apply — the chat is always
    // in-flow and there is no toggle button to show.
    const inlineSelf = parseBooleanAttribute(this, "inline");
    const autoOpen = !isScreenSizeMobile() && parseBooleanAttribute(this, "auto-open");
    const fullscreen = parseBooleanAttribute(this, "fullscreen");
    const hideToggleButton = inlineSelf || parseBooleanAttribute(this, "hide-toggle-button");

    // The widget always renders into an inline host child div — that child is the
    // shadow root's host. By default the host is appended to `<lastbot-chat>` itself,
    // so wherever you place the element is where the widget renders. The `container=`
    // attribute is an override that mounts the inline host inside a different element
    // (and hides `<lastbot-chat>` since it becomes redundant in that case).
    //
    // Visible UX is still controlled by `inline` below: floating-overlay vs in-flow.
    const containerSelector = this.getAttribute("container");
    if (this.hasAttribute("container")) {
      // Deprecated in favour of `[data-lastbot]`, which renders the chat in-flow
      // into its own element (no separate target, no hidden marker). `container=`
      // still works for now. Gated on presence (not a truthy value) so an empty
      // `container=""` is flagged too. Fires once per real non-headless mount:
      // headless mode returns earlier and ignores `container=`, and reconnects /
      // DOM-moves early-return on the existing coreInstance above. Removal is a
      // future release.
      console.warn(
        '[lastbot-chat] The "container" attribute is deprecated and will be removed in a future release. ' +
          'Embed with <div data-lastbot data-widget-id="…" data-base-url="…"> instead (renders in-flow). ' +
          "See docs/api.md#deprecations.",
      );
    }
    const containerTarget = containerSelector
      ? document.querySelector(containerSelector)
      : null;
    if (containerSelector && !containerTarget) {
      console.warn(
        `[lastbot-chat] container="${containerSelector}" did not match any element; falling back to in-place placement`,
      );
    }
    const inlineMode = containerTarget !== null || inlineSelf;

    const host = document.createElement("div");
    // Inline/container mode: host fills its container so the in-flow chat
    // panel renders at the container's size.
    //
    // Overlay mode (default): both the toggle button and the chat panel are
    // position:fixed (anchored to the viewport), so the host doesn't need to
    // reserve any layout space. Without this guard, an embedder who placed
    // <lastbot-chat> inside a sized container (flex-grow:1, height:100vh,
    // grid cell, …) ended up with an invisible 100%×100% block that absorbed
    // the available area — most visible with `hide-toggle-button="true"`
    // where the toggle button is no longer there to mask the empty region.
    host.style.cssText = inlineMode
      ? "display:block;width:100%;height:100%;position:relative;"
      : "display:block;width:0;height:0;";
    host.setAttribute("data-lastbot-host", "inline");
    (containerTarget ?? this).appendChild(host);
    this.inlineHost = host;
    if (containerTarget) {
      // The <lastbot-chat> element is just a marker in its original location now;
      // hide it so it doesn't take up flow.
      this.style.display = "none";
    } else if (inlineSelf) {
      // In-flow into ourselves: make <lastbot-chat> fill the [data-lastbot]
      // container it was synthesized into, so the in-flow chat gets a real box.
      // (The embedder sizes that container.)
      this.style.display = "block";
      this.style.width = "100%";
      this.style.height = "100%";
    }

    const shadow = host.attachShadow({ mode: "open" });

    const livekitStyleSheet = document.createElement("style");
    livekitStyleSheet.textContent = livekitStyles;

    const appStyleSheet = document.createElement("style");
    appStyleSheet.textContent = styles;

    const container = document.createElement("div");
    container.id = "lastbot-chat-root";

    shadow.appendChild(livekitStyleSheet);
    shadow.appendChild(appStyleSheet);
    shadow.appendChild(container);

    // Host-page overrides: structural templates (parsed once at init) and external CSS
    // (fetched once; browser cache covers repeats). Styling is awaited together with
    // core.init() before dispatching lastbot:ready so listeners see a fully-ready widget.
    // Scope-root-first, global-fallback. The scope root ([data-lastbot] container or
    // this <lastbot-chat> element itself) may declare its own templates/styles, scoped
    // to this instance; otherwise the document-global set is used. See docs/api.md
    // § Template + CSS overrides.
    const scopeContainer = scopeRootFor(this);
    const templates = resolveScopedTemplates(this);
    // Template-driven single-page mode: when the embedder declares
    // <template id="lastbot-custom-main-page">, the widget renders that
    // template instead of cycling through Landing → CustomerInquiry →
    // Conversation. See docs/api.md § Embedding modes.
    const hasCustomMainPage = templates.has("lastbot-custom-main-page");
    const stylesheetUrl = this.getAttribute("stylesheet-url") || undefined;
    const stylingPromise = applyHostStyling(shadow, {
      stylesheetUrl,
      inlineStyleId: "lastbot-styles",
      container: scopeContainer,
    });

    // Capture the local core ref (see headless branch above). Styling promise is
    // wrapped in .catch so a stylesheet failure can't block lifecycle dispatch —
    // applyHostStyling already logs internally; a missed stylesheet shouldn't
    // mean lastbot:ready never fires.
    const initCore = this.coreInstance;

    // Brand-color bridge. Sets --brand-primary-color as an inline style on
    // the shadow host once brand info arrives. The bundled index.css resolves
    // --lastbot-color-group-brand as `var(--brand-primary-color, <default>)`,
    // which then flows through every brand-derived semantic. Embedder overrides
    // of --lastbot-color-group-brand or any --lastbot-{role}-color in
    // <style id="lastbot-styles"> still win because they redefine the *consumed*
    // variable, bypassing the var() chain — no !important needed.
    //
    // Launcher colours (LAS-4753) follow the same rule. The Hub's
    // brand.colors.toggle_primary / toggle_accent, plus a darkened hover shade
    // of each, go on the host under internal names (--brand-toggle-*), never
    // under the public --lastbot-slot-toggle-button-* names: an inline style on
    // the host beats a `:host { … }` rule in the embedder's stylesheet, so
    // setting the public name here would silently defeat an embedder override.
    // Each property travels with a matching host attribute
    // (data-brand-toggle-primary, -primary-strong, -accent, -accent-strong),
    // always added and removed together. index.css gates
    // `:host([data-brand-toggle-X]) [data-slot-name="toggle-button"]` rules on
    // it that re-declare the global semantic (--lastbot-primary-color, …) on the
    // slot element from --brand-toggle-*. Those rules live in the base layer, so
    // the precedence is: the embedder's slot variable
    // --lastbot-slot-toggle-button-* (mechanism A) > an embedder's
    // [data-slot-name="toggle-button"] { --lastbot-*-color } rule (mechanism B,
    // unlayered) > Hub launcher colour > global semantic and bundled default.
    //
    // CSS.supports() guards against malformed-but-truthy values (e.g. "#",
    // "redd"). Without this check, the inline custom property would still be
    // "set" — just to garbage — and the var() fallback in index.css would NOT
    // fire, breaking every utility that reads it until the next reload.
    //
    // Cache the last-applied value per key so we don't re-touch host.style on
    // every state mutation (typing, scrolling, agent presence, etc.).
    // `undefined` means "no value applied yet"; null means "explicitly removed".
    const supportedColor = (value: string | undefined): string | null =>
      value && CSS.supports("color", value) ? value : null;
    const applyHostColor = (name: string, value: string | null): void => {
      if (value != null) {
        host.style.setProperty(name, value);
      } else {
        host.style.removeProperty(name);
      }
    };
    // A launcher colour is the host property plus its gating host attribute;
    // index.css reads the property only while the attribute is present, so the
    // two are always set and removed together.
    const applyGatedHostColor = (
      name: string,
      gateAttribute: string,
      value: string | null,
    ): void => {
      applyHostColor(name, value);
      if (value != null) {
        host.setAttribute(gateAttribute, "");
      } else {
        host.removeAttribute(gateAttribute);
      }
    };
    // The strong (hover) shade is derived only from hex input; for a named or
    // functional colour darkenHex() returns null and the bundled strong
    // default applies.
    const applyToggleColor = (
      role: "primary" | "accent",
      value: string | null,
    ): void => {
      applyGatedHostColor(`--brand-toggle-${role}-color`, `data-brand-toggle-${role}`, value);
      applyGatedHostColor(
        `--brand-toggle-${role}-strong-color`,
        `data-brand-toggle-${role}-strong`,
        value != null ? darkenHex(value) : null,
      );
    };
    let lastAppliedPrimary: string | null | undefined = undefined;
    let lastAppliedTogglePrimary: string | null | undefined = undefined;
    let lastAppliedToggleAccent: string | null | undefined = undefined;
    this.brandBridgeCleanup = initCore.subscribe(() => {
      const colors = initCore.getState().brand?.colors;
      const primary = supportedColor(colors?.primary);
      if (primary !== lastAppliedPrimary) {
        lastAppliedPrimary = primary;
        applyHostColor("--brand-primary-color", primary);
      }
      const togglePrimary = supportedColor(colors?.toggle_primary);
      if (togglePrimary !== lastAppliedTogglePrimary) {
        lastAppliedTogglePrimary = togglePrimary;
        applyToggleColor("primary", togglePrimary);
      }
      const toggleAccent = supportedColor(colors?.toggle_accent);
      if (toggleAccent !== lastAppliedToggleAccent) {
        lastAppliedToggleAccent = toggleAccent;
        applyToggleColor("accent", toggleAccent);
      }
    });

    // Size bridge. core.setSize() emits `widget:set-size`; we apply it to the
    // overlay (desktop popup) dimension vars on the shadow host, where the
    // index.css defaults live. The container transitions width/height, so the
    // change animates. `null` resets a dimension to its default; an omitted key
    // leaves the current value untouched. CSS.supports() rejects malformed
    // lengths so a bad value can't clobber the working default.
    const applySize = (size: {
      width?: string | null;
      height?: string | null;
      maxHeight?: string | null;
      contentMaxWidth?: string | null;
    }): void => {
      setHostLengthVar(host, "width", "--lastbot-widget-width", size.width);
      setHostLengthVar(host, "height", "--lastbot-widget-height", size.height);
      setHostLengthVar(host, "max-height", "--lastbot-widget-max-height", size.maxHeight);
      setHostLengthVar(host, "max-width", "--lastbot-content-max-width", size.contentMaxWidth);
    };
    this.setSizeCleanup = initCore.on("widget:set-size", applySize);

    // Position: mirror applySize for the panel's offset vars. Each edge is set on
    // the shadow host inline (overriding the responsive index.css defaults at every
    // breakpoint); `null` resets it to the default; an omitted key is untouched.
    const applyPosition = (pos: {
      top?: string | null;
      right?: string | null;
      bottom?: string | null;
      left?: string | null;
    }): void => {
      const edges = ["top", "right", "bottom", "left"] as const;
      for (const edge of edges) {
        setHostLengthVar(host, edge, `--lastbot-widget-offset-${edge}`, pos[edge]);
      }
    };
    this.setPositionCleanup = initCore.on("widget:set-position", applyPosition);

    void Promise.all([
      initCore.init(),
      stylingPromise.catch(() => undefined),
    ]).then(([initResult]) => this.dispatchLifecycleEvent(initResult, initCore));

    // Loads custom font in case it's not already present in the document head
    if (!document.querySelector('link[href*="fonts.googleapis.com"][href*="family=Inter"]')) {
      const fontLink = document.createElement("link");
      fontLink.href =
        "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap";
      fontLink.rel = "stylesheet";
      document.head.appendChild(fontLink);
    }

    this.root = createRoot(container);
    this.root.render(
      <TemplatesProvider templates={templates}>
        <App
          core={this.coreInstance}
          autoOpen={autoOpen}
          fullscreen={fullscreen}
          widgetId={widgetId}
          placementId={placementId || undefined}
          startMessage={startMessage}
          userEmail={userEmail}
          hideToggleButton={hideToggleButton}
          homeUrl={homeUrl}
          skipIngest={skipIngest}
          hubIdentity={hubIdentity}
          inline={inlineMode}
          hasCustomMainPage={hasCustomMainPage}
        />
      </TemplatesProvider>,
    );
  }

  /**
   * Dispatch the appropriate lifecycle event after init + styling resolve.
   *
   * - Success → `lastbot:ready` with `{ core }`.
   * - Failure → `lastbot:error` with `{ core, status }` (no `lastbot:ready`).
   *
   * Fired on `window`, matching the headless dispatch from `LastBotCore` —
   * embedders use a single `window.addEventListener("lastbot:ready", …)`
   * pattern regardless of how the widget was instantiated. The custom
   * element path waits for the stylesheet injection to settle before
   * firing; the core path fires immediately on init resolve.
   *
   * Guards against three race conditions:
   *  1. Element disconnected mid-init → no host to back the `core` ref; skip.
   *  2. Element disconnected + reconnected with a fresh core mid-init → the stale
   *     promise must NOT dispatch using the new core; we compare `initCore` against
   *     the current `this.coreInstance`.
   *  3. Element destroyed (`coreInstance === null`) → never dispatch with null core.
   */
  private dispatchLifecycleEvent(result: InitResult, initCore: LastBotCore): void {
    if (this.coreInstance !== initCore) return;
    if (!this.isConnected) return;
    // widgetId/placementId let host-page listeners tell placements apart without
    // reaching into detail.core. placementId is omitted when the embedder didn't set one.
    const ids = {
      widgetId: initCore.config.widgetId,
      placementId: initCore.config.placementId,
    };
    const detail = result.success
      ? { core: initCore, ...ids }
      : { core: initCore, status: result.status, ...ids };
    window.dispatchEvent(
      new CustomEvent(result.success ? "lastbot:ready" : "lastbot:error", {
        detail,
      }),
    );
  }

  public disconnectedCallback(): void {
    // Web Components fire disconnect/connect synchronously during a DOM move
    // (e.g. `elementB.appendChild(existingLastBotChat)`). Defer real teardown by one
    // microtask: if we're back in the DOM by then, this was a move and we keep state.
    queueMicrotask(() => {
      if (this.isConnected) return;
      // Capture the core we're about to tear down so we can announce it after
      // teardown finishes. Null when the element never initialized (e.g. it
      // bailed on missing required attributes) — no widget existed, so we stay
      // silent below.
      const destroyedCore = this.coreInstance;
      if (this.root) {
        this.root.unmount();
        this.root = null;
      }
      if (this.brandBridgeCleanup) {
        this.brandBridgeCleanup();
        this.brandBridgeCleanup = null;
      }
      if (this.setSizeCleanup) {
        this.setSizeCleanup();
        this.setSizeCleanup = null;
      }
      if (this.setPositionCleanup) {
        this.setPositionCleanup();
        this.setPositionCleanup = null;
      }
      if (this.coreInstance) {
        this.coreInstance._setHostElement(null);
        this.coreInstance.destroy();
        this.coreInstance = null;
      }
      if (this.placementKey !== null && destroyedCore) {
        unregisterPlacement(this.placementKey, destroyedCore);
        this.placementKey = null;
      }
      if (this.inlineHost) {
        this.inlineHost.remove();
        this.inlineHost = null;
      }
      // Container mode hides the <lastbot-chat> element via inline style, and
      // inline (in-flow) mode sizes it to fill its parent. If the element is
      // reused later in a different mode, leftover inline styles would silently
      // break placement. Clear them on teardown so it's a clean slate.
      this.style.removeProperty("display");
      this.style.removeProperty("width");
      this.style.removeProperty("height");
      this.headlessMode = false;

      // Teardown lifecycle event, mirroring lastbot:ready / lastbot:error and
      // fired on `window` like them. Embedders using the [data-lastbot] pattern
      // hold no reference to this synthesized element, so this is their only
      // hook to observe teardown (drop their own listeners, update UI, log).
      // Only fired when a real widget was torn down; DOM moves return early
      // above and never reach here.
      if (destroyedCore) {
        window.dispatchEvent(
          new CustomEvent("lastbot:destroyed", {
            detail: {
              core: destroyedCore,
              widgetId: destroyedCore.config.widgetId,
              placementId: destroyedCore.config.placementId,
            },
          }),
        );
      }
    });
  }
}

/**
 * Attributes that never apply to the `[data-lastbot]` embedded path, so they are
 * NOT forwarded onto the synthesized `<lastbot-chat>`:
 *  - `data-lastbot` — the marker itself, not config;
 *  - `data-container` — the [data-lastbot] element IS the render target (in-flow
 *    into itself); a separate container would contradict that;
 *  - `data-hide-toggle-button`, `data-auto-open`, `data-fullscreen` — overlay-only
 *    settings with no meaning when the chat is embedded in-flow (no toggle, always
 *    shown, never fullscreen);
 *  - `data-inline` — `scanAndMount` forces `inline="true"`; forwarding a
 *    `data-inline="false"` would override that and break the always-in-flow contract.
 */
const NON_FORWARDED_DATA_ATTRS = new Set([
  "data-lastbot",
  "data-container",
  "data-hide-toggle-button",
  "data-auto-open",
  "data-fullscreen",
  "data-inline",
]);

/**
 * Copy a `[data-lastbot]` container's `data-*` attributes onto a freshly created
 * `<lastbot-chat>` element, stripping the `data-` prefix, minus the attributes in
 * `NON_FORWARDED_DATA_ATTRS`. There is deliberately no allow-list of forwarded
 * names: whatever else the container declares (widget-id, base-url, user-email,
 * stylesheet-url, …) is forwarded automatically, including attributes added later.
 */
export function applyContainerConfig(el: HTMLElement, container: Element): void {
  for (const attr of Array.from(container.attributes)) {
    if (!attr.name.startsWith("data-")) continue;
    if (NON_FORWARDED_DATA_ATTRS.has(attr.name)) continue;
    el.setAttribute(attr.name.slice("data-".length), attr.value);
  }
}

/**
 * The scope root a `<lastbot-chat>` reads its own templates/styles from:
 *  - the `[data-lastbot]` container it was mounted into (embedded path — the templates
 *    are authored as children of that container, siblings of this synthesized element); or
 *  - the `<lastbot-chat>` element itself (standalone overlay / hand-authored inline —
 *    templates are authored as its own children).
 * Either way the widget reads THIS element's own child `<template>`/`<style>` blocks, so
 * the same markup is copy-pasteable between a `[data-lastbot]` and a `<lastbot-chat>`.
 */
function scopeRootFor(el: HTMLElement): HTMLElement {
  // The synthesized element is a direct child of its [data-lastbot] container, but use
  // closest() (not just parentElement) so a hand-authored <lastbot-chat> nested deeper
  // inside a [data-lastbot] still resolves to that container rather than silently
  // scoping to itself. A standalone <lastbot-chat> (no [data-lastbot] ancestor) is its
  // own scope root.
  return el.closest<HTMLElement>("[data-lastbot]") ?? el;
}

// Scope roots already warned about shadowing globals — so a page with N placements (each
// mounting once, plus StrictMode/HMR re-runs) warns at most once per scope root instead of
// on every mount. WeakSet so detached scope roots are collectable.
const shadowWarnedRoots = new WeakSet<HTMLElement>();

/**
 * Scoped template resolution is all-or-nothing: a scope root that declares ANY `lastbot-*`
 * template uses ONLY its own set — the document-global templates are NOT merged in for this
 * instance. That surprises embedders who expect scoping to be additive (declare a scoped
 * welcome-text and still inherit a global navbar). Emit a one-time, dev-oriented note listing
 * the silently-dropped globals; harmless if the drop was intentional. Kept to once per scope
 * root (and skipped entirely when there are no global templates) to avoid per-mount noise and
 * a repeated full-document scan.
 */
function warnIfShadowingGlobals(container: HTMLElement, scoped: Map<string, ReactNode>): void {
  if (shadowWarnedRoots.has(container)) return;
  shadowWarnedRoots.add(container);
  const shadowed = [...discoverTemplates(document).keys()].filter(id => !scoped.has(id));
  if (shadowed.length === 0) return;
  console.info(
    `[lastbot-chat] This placement declares its own scoped template(s), so the ` +
      `document-global template(s) [${shadowed.join(", ")}] do NOT apply to it — scoped ` +
      `templates are all-or-nothing, not merged with the global set. Re-declare them inside ` +
      `this scope root to keep them here, or ignore this if the drop is intentional. ` +
      `See docs/api.md § Template + CSS overrides.`,
  );
}

/**
 * Resolve the template set for a `<lastbot-chat>` element: scope-root-first, with a
 * document-global fallback (all-or-nothing). If the element's scope root DECLARES any
 * `lastbot-*` template, only that scope root's templates are used (a typo there stays
 * scoped and surfaces a warning rather than silently reverting to global). Otherwise the
 * document-global set (templates outside any scope root) is used.
 */
export function resolveScopedTemplates(el: HTMLElement): Map<string, ReactNode> {
  const container = scopeRootFor(el);
  if (containerDeclaresTemplates(container)) {
    const scoped = discoverTemplates(container);
    warnIfShadowingGlobals(container, scoped);
    return scoped;
  }
  return discoverTemplates(document);
}

/**
 * Find `[data-lastbot]` containers that don't already hold a widget and mount a
 * `<lastbot-chat>` into each.
 *
 * "Already mounted" is detected by the presence of a live `<lastbot-chat>` direct
 * child rather than a marker attribute. This is idempotent (repeated
 * `lastbot:container-ready` events, React StrictMode double-effects, and the
 * on-load + DOMContentLoaded scans all skip a container that already has a child
 * — the child exists as soon as `appendChild` returns, before any re-entrant
 * scan can run) AND self-healing: if the child is torn down while the container
 * node survives, the container becomes mountable again on the next scan/event.
 * (A one-shot marker would strand such a container forever.)
 *
 * The synthesized element runs the normal custom-element lifecycle, so mount
 * (connectedCallback) and teardown-on-removal (disconnectedCallback) are reused
 * unchanged. When a React/SPA host unmounts the container, the child
 * `<lastbot-chat>` disconnects and tears down cleanly — no leak on route change.
 *
 * The `[data-lastbot]` path is always the embedded (in-flow) style: the
 * synthesized element gets `inline="true"`, so the chat fills the container div
 * rather than floating as an overlay. For an overlay, write `<lastbot-chat>`
 * directly — it upgrades order-independently on its own.
 */
export function scanAndMount(): void {
  const containers = document.querySelectorAll<HTMLElement>("[data-lastbot]");
  for (const container of Array.from(containers)) {
    if (container.querySelector(":scope > lastbot-chat")) continue;
    const el = document.createElement("lastbot-chat");
    el.setAttribute("inline", "true");
    applyContainerConfig(el, container);
    container.appendChild(el);
  }
}

if (!customElements.get("lastbot-chat")) {
  customElements.define("lastbot-chat", Widget);
}

// Order-independent `[data-lastbot]` embedding. Three complementary triggers:
//  1. immediate scan — container rendered before this bundle executed;
//  2. DOMContentLoaded scan — bundle ran in <head> before <body> was parsed;
//  3. lastbot:container-ready — container rendered after the bundle loaded
//     (React/SPA hosts dispatch this from an effect once their container mounts).
// All idempotent (scanAndMount skips containers that already hold a widget), and
// independent of whether any <lastbot-chat> element exists.
if (typeof window !== "undefined" && typeof document !== "undefined") {
  window.addEventListener("lastbot:container-ready", scanAndMount);
  if (document.readyState === "loading") {
    // Fires at most once, right after parsing; drop the listener afterward.
    document.addEventListener("DOMContentLoaded", scanAndMount, { once: true });
  }
  scanAndMount();
}
